import { deadAfterFor, waitingPatchFromUids } from './asyncPlay.js';
import {
  ref as dbRef, onValue, onDisconnect, push, set, get, update, remove, runTransaction,
} from 'firebase/database';
import { db } from '../firebase';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { MIN_PLAYERS, MAX_PLAYERS, pageCount, bookHeldBy, kindOfPage, hasSubmitted } from '../engine/rounds';
import { BOT_NAMES } from './botBrain';
import { PHASES, revealDriver } from '../engine/game';

export const GAMES = 'telestrationsGames';
export const CODES = 'telestrationsGameCodes';
export const USER_GAMES = 'telestrationsUserGames';

// Rooms clean themselves up, same arrangement as no-thanks: presence plus an
// age limit, swept by each client for its own rooms when the lobby opens.
export const DEAD_AFTER_MS = {
  lobby: 60 * 60 * 1000,
  playing: 24 * 60 * 60 * 1000,
  reveal: 6 * 60 * 60 * 1000,
};

/**
 * WHY THE STATE IS SPLIT UP, unlike no-thanks.
 *
 * No Thanks! keeps its whole game state in one JSON string and rewrites it on
 * every move. That works there because the state is a few hundred bytes and
 * only one person moves at a time.
 *
 * Neither is true here. This game is SIMULTANEOUS - a dozen people submit at
 * once - and the state is mostly drawings, so a finished twelve-player game is
 * a few hundred kilobytes. Rewriting all of it on every submit would mean a
 * dozen clients fighting over one node, each pushing the whole game through
 * the wire, dozens of times per round.
 *
 * So pages are written individually, at `pages/<book>/<step>`, by exactly the
 * one person holding that book on that step. Two players never write the same
 * key, so there is no contention and no transaction needed on the hot path.
 * The small shared bits - which step we're on, where the reveal is - live in
 * their own little nodes and change once per round rather than once per
 * player per round.
 *
 * Each page is stored as a JSON STRING. RTDB has no faithful representation
 * for the nested arrays a drawing is made of (a stroke's points are [x, y]
 * pairs), and it silently drops empty ones. Stringifying sidesteps the whole
 * question.
 */
const encodePage = (page) => JSON.stringify(page);
const decodePage = (raw) => {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
};

/** Turns the flat `pages/<book>/<step>` map back into the engine's book array. */
export const booksFromPages = (players, pagesNode) => players.map((player, index) => ({
  owner: index,
  ownerName: player.name,
  pages: Object.entries(pagesNode?.[index] || {})
    .reduce((pages, [step, raw]) => {
      // eslint-disable-next-line no-param-reassign
      pages[Number(step)] = decodePage(raw);
      return pages;
    }, []),
}));

/** The engine-shaped state, assembled from the room's separate nodes. */
export const gameFromRoom = (meta, round, pagesNode, reveal) => {
  if (!meta || !round) return null;
  const players = [...(meta.playerSlots || [])]
    .sort((a, b) => a.seatIndex - b.seatIndex)
    .map((slot, index) => ({
      id: slot.uid, name: slot.displayName, seat: index, isBot: Boolean(slot.isBot),
    }));
  return {
    phase: round.phase,
    step: round.step ?? 0,
    pages: round.pages ?? pageCount(players.length),
    players,
    books: booksFromPages(players, pagesNode),
    reveal: reveal || { book: 0, page: 0 },
  };
};

export const seatOf = (game, uid) => game?.players?.findIndex((p) => p.id === uid) ?? -1;

/**
 * Who nudges the round along.
 *
 * Advancing a step is a shared act with no natural owner, so exactly one
 * client has to do it or the step number jumps by more than one. Same rule as
 * no-thanks: the connected player in the lowest seat, derived from state every
 * client already has, so nobody has to negotiate and it survives the host
 * leaving.
 */
/**
 * Who turns the pages during the reveal.
 *
 * The owner of the book on screen turns their own pages - Matt's call, and it
 * is the right one: it is their book being read out.
 *
 * But a robot has no browser. Bug report (2026-08-27): "When a robot is
 * turning the pages they take too long. The host should turn pages for the
 * robots." It was worse than slow - when a robot's book came up, no client
 * matched the owner, so the reveal rendered no controls at all and could not
 * be advanced from any device.
 *
 * So: the owner if they are a person and they are here, otherwise the host,
 * otherwise the lowest-seated connected human. That last fallback is what
 * makes it impossible to wedge again for want of somebody to press the button.
 * Returns a uid, or null when literally nobody is connected.
 */
export const revealPageTurner = (game, presence, hostUid) => {
  const connected = (uid) => Boolean(presence?.[uid]);
  const ownerSeat = revealDriver(game);
  const owner = (game?.players || []).find((p) => p.seat === ownerSeat);
  if (owner && !owner.isBot && connected(owner.id)) return owner.id;

  if (hostUid && connected(hostUid)) return hostUid;
  const humans = (game?.players || []).filter((p) => !p.isBot && connected(p.id));
  if (!humans.length) return null;
  return humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id;
};

export const stepDriver = (game, presence) => {
  const connected = (game?.players || []).filter((p) => presence?.[p.id]);
  if (!connected.length) return null;
  return connected.reduce((best, p) => (p.seat < best.seat ? p : best)).id;
};

export const lastActiveOf = (meta) => {
  if (Number.isFinite(meta?.lastActiveAt)) return meta.lastActiveAt;
  const parsed = Date.parse(meta?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isDeadRoom = (meta, presence, now = Date.now()) => {
  if (!meta) return false;
  if (Object.keys(presence || {}).length > 0) return false;
  // An async room (asyncPlay.js) is meant to sit for days; it gets the long limits.
  const limit = deadAfterFor(meta, DEAD_AFTER_MS);
  return now - lastActiveOf(meta) > limit;
};

// ---- room lifecycle --------------------------------------------------------

/** A bot seat, which looks like a player slot with a made-up uid. */
export const botSlot = (index, seatIndex) => ({
  uid: `bot-${index}`,
  displayName: BOT_NAMES[index % BOT_NAMES.length],
  seatIndex,
  isBot: true,
});

/**
 * Sets how many bots sit at the table. Host only, lobby only.
 * Bots always take the last seats, so the humans keep theirs.
 */
export const setBotCount = async ({ user, gameId, meta, count }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can change that.');
  if (meta.status !== 'lobby') throw new Error('The game has already started.');
  const humans = (meta.playerSlots || []).filter((s) => !s.isBot);
  const wanted = Math.max(0, Math.min(count, MAX_PLAYERS - humans.length));
  const slots = [
    ...humans.map((s, i) => ({ ...s, seatIndex: i })),
    ...Array.from({ length: wanted }, (_, i) => botSlot(i, humans.length + i)),
  ];
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, lastActiveAt: Date.now() });
};

/**
 * Telestrations' async adapter (asyncPlay.js): the round waits on every
 * human who has not handed in a page for this step; bots are drawn for by
 * the lowest-seat human, so a round where only bots are left waits on them.
 * The reveal waits on nobody (everyone looks together, whenever).
 */
export const asyncWaitingOn = (game) => {
  if (!game || game.phase !== PHASES.PLAYING) return [];
  const pending = game.players.filter((p) => !hasSubmitted(game, p.seat));
  const humans = pending.filter((p) => !p.isBot).map((p) => p.id);
  if (humans.length) return humans;
  const anyHuman = game.players.filter((p) => !p.isBot);
  return pending.length && anyHuman.length ? [anyHuman.reduce((best, p) => (p.seat < best.seat ? p : best)).id] : [];
};

/** After a page or an advance in an async room: who the round waits on now. */
export const recordWaiting = async ({ gameId, game, meta }) => {
  if (!db || !meta?.async || !game) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), waitingPatchFromUids(asyncWaitingOn(game), meta)).catch(() => {});
};

export const createRoom = async ({ user, async: asyncRoom = false }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const gameRef = push(dbRef(db, GAMES));
  const gameId = gameRef.key;

  let code;
  let attempts = 0;
  do {
    code = generateRoomCode(attempts);
    attempts += 1;
    // eslint-disable-next-line no-await-in-loop
  } while (attempts < 12 && (await get(dbRef(db, `${CODES}/${code}`))).exists());

  const meta = {
    hostUid: user.uid,
    status: 'lobby',
    async: asyncRoom,
    roomCode: code,
    createdAt: new Date().toISOString(),
    lastActiveAt: Date.now(),
    playerSlots: [{ uid: user.uid, displayName: user.displayName || 'Host', seatIndex: 0 }],
  };
  await set(dbRef(db, `${GAMES}/${gameId}/meta`), meta);
  await set(dbRef(db, `${CODES}/${code}`), gameId);
  await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'host', joinedAt: meta.createdAt });
  return { gameId, code };
};

export const joinRoom = async ({ user, code }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (!user) throw new Error('Sign in first.');

  const normalized = normalizeRoomCode(code);
  const codeSnap = await get(dbRef(db, `${CODES}/${normalized}`));
  if (!codeSnap.exists()) throw new Error(`No game found for code "${normalized}".`);
  const gameId = codeSnap.val();

  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) throw new Error('That game no longer exists.');
  const meta = metaSnap.val();
  const slots = meta.playerSlots || [];

  if (!slots.some((s) => s.uid === user.uid)) {
    // Once the books are moving, a new person can't be slotted in: everyone's
    // chain is already the length of the table. Rejoining a seat you already
    // hold is always fine, which is how coming back from a dropped connection
    // works.
    if (meta.status !== 'lobby') throw new Error('That game has already started.');
    // Humans sit before bots, so a late joiner takes a seat rather than
    // queueing behind a robot.
    const humans = slots.filter((s) => !s.isBot);
    const bots = slots.filter((s) => s.isBot);
    humans.push({ uid: user.uid, displayName: user.displayName || 'Guest', seatIndex: humans.length });
    slots.length = 0;
    slots.push(
      ...humans.map((s, i) => ({ ...s, seatIndex: i })),
      ...bots.map((s, i) => ({ ...s, seatIndex: humans.length + i })),
    );
    await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, lastActiveAt: Date.now() });
    await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
  }
  return { gameId };
};

export const leaveLobby = async ({ user, gameId }) => {
  if (!db || !user || !gameId) return;
  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) return;
  const meta = metaSnap.val();
  if (meta.status !== 'lobby') return;
  const slots = (meta.playerSlots || [])
    .filter((s) => s.uid !== user.uid)
    .map((s, i) => ({ ...s, seatIndex: i }));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots });
  await remove(dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`));
};

export const startRoom = async ({ user, gameId, meta }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can start.');
  const seats = (meta.playerSlots || []).length;
  if (seats < MIN_PLAYERS) throw new Error(`Telestrations needs at least ${MIN_PLAYERS} players.`);

  await set(dbRef(db, `${GAMES}/${gameId}/round`), {
    step: 0, phase: PHASES.PLAYING, pages: pageCount(seats),
  });
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    status: 'playing',
    lastActiveAt: Date.now(),
    // An async room waits on everyone from the first page (asyncPlay.js).
    ...(meta.async ? waitingPatchFromUids((meta.playerSlots || []).filter((s) => !s.isBot).map((s) => s.uid), meta) : {}),
  });
};

// ---- play ------------------------------------------------------------------

/**
 * Writes one page. Only the person holding that book on that step can, and
 * only once - the `!data.exists()` rule on the node enforces the second part
 * server-side, so a double tap or a second tab can't overwrite a drawing.
 */
export const submitPage = async ({ gameId, game, seat, content }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const book = bookHeldBy(seat, game.step, game.players.length);
  const player = game.players[seat];
  const page = {
    kind: kindOfPage(game.step),
    by: player?.id ?? null,
    byName: player?.name ?? null,
    content,
    at: Date.now(),
  };
  await set(dbRef(db, `${GAMES}/${gameId}/pages/${book}/${game.step}`), encodePage(page));
  update(dbRef(db, `${GAMES}/${gameId}/meta`), { lastActiveAt: Date.now() }).catch(() => {});
};

/**
 * Moves the room to the next step, or into the reveal.
 *
 * A transaction, and it re-checks the step it is advancing FROM. Several
 * clients can notice "everyone's done" in the same instant; without this the
 * step would jump by however many of them noticed.
 */
export const advanceRound = async ({ gameId, fromStep, totalPages }) => {
  if (!db) return { committed: false };
  // Warm the cache with a LISTENER, not a get().
  //
  // A transaction whose handler returns `undefined` is aborted outright, never
  // retried. Firebase calls that handler once with whatever is in the local
  // sync tree - which is null on a client that has never listened here - so
  // aborting on null kills the advance permanently instead of retrying against
  // the server.
  //
  // `get()` does NOT populate that sync tree; only a subscription does.
  // Verified against the real database: after a successful get() returning
  // {step: 0}, the transaction handler was still handed null and the round
  // never moved. In normal play bindRoom already holds a listener on this
  // node, so this is belt and braces - but the failure it prevents is a game
  // that silently stops advancing, which is worth one extra subscription.
  // The listener must stay attached ACROSS the transaction. Detaching it as
  // soon as the first value arrives evicts the node from the sync tree again,
  // which puts us straight back to the handler being handed null.
  const roundRef = dbRef(db, `${GAMES}/${gameId}/round`);
  let detach = () => {};
  await new Promise((resolve) => {
    detach = onValue(roundRef, () => resolve());
  });
  const result = await runTransaction(roundRef, (round) => {
    if (!round || round.step !== fromStep || round.phase !== PHASES.PLAYING) return undefined;
    const step = round.step + 1;
    if (step >= totalPages) return { ...round, step: totalPages, phase: PHASES.REVEAL };
    return { ...round, step };
  });
  detach();
  return { committed: result.committed };
};

/** The shared reveal position, driven by whoever's book is on screen. */
export const setReveal = async ({ gameId, reveal }) => {
  if (!db) return;
  await set(dbRef(db, `${GAMES}/${gameId}/reveal`), reveal);
};

export const bindRoom = ({ gameId, user, onMeta, onRound, onPages, onReveal, onPresence }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const at = (path) => dbRef(db, `${GAMES}/${gameId}/${path}`);
  const offs = [
    onValue(at('meta'), (s) => onMeta(s.val())),
    onValue(at('round'), (s) => onRound(s.val())),
    onValue(at('pages'), (s) => onPages(s.val() || {})),
    onValue(at('reveal'), (s) => onReveal(s.val())),
    onValue(at('presence'), (s) => onPresence(s.val() || {})),
  ];

  let myPresenceRef = null;
  if (user) {
    myPresenceRef = at(`presence/${user.uid}`);
    onDisconnect(myPresenceRef).remove();
    set(myPresenceRef, true);
    update(at('meta'), { lastActiveAt: Date.now() }).catch(() => {});
  }

  return () => {
    offs.forEach((off) => off());
    if (myPresenceRef) remove(myPresenceRef);
  };
};

/**
 * Deletes the caller's own dead rooms. Self-service for the same reason as in
 * no-thanks: the rules grant read per room and never on the collection, so no
 * client can list every room, and only a host may delete one.
 */
export const sweepMyRooms = async ({ user, now = Date.now(), database = db }) => {
  if (!database || !user) return { deleted: 0, pruned: 0 };
  let deleted = 0;
  let pruned = 0;
  const mine = await get(dbRef(database, `${USER_GAMES}/${user.uid}`));
  if (!mine.exists()) return { deleted, pruned };

  for (const [gameId, entry] of Object.entries(mine.val() || {})) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const metaSnap = await get(dbRef(database, `${GAMES}/${gameId}/meta`));
      if (!metaSnap.exists()) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`));
        pruned += 1;
        continue;
      }
      if (entry?.role !== 'host' && metaSnap.val().hostUid !== user.uid) continue;
      // eslint-disable-next-line no-await-in-loop
      const presenceSnap = await get(dbRef(database, `${GAMES}/${gameId}/presence`));
      const meta = metaSnap.val();
      if (!isDeadRoom(meta, presenceSnap.val() || {}, now)) continue;
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${GAMES}/${gameId}`));
      if (meta.roomCode) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${CODES}/${meta.roomCode}`)).catch(() => {});
      }
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`)).catch(() => {});
      deleted += 1;
    } catch {
      // Never let one room stop the sweep.
    }
  }
  return { deleted, pruned };
};
